// Tenantra Inc. - Shared skeleton for product sub-pages (Rent Reporting / Rent Collection / Screening)
import { useState } from 'react';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { PublicNav } from './PublicNav';
import { PublicFooter } from './PublicFooter';

const EYEBROW = 'text-xs font-semibold uppercase tracking-[0.18em] text-teal-700';

export const Section = ({ id, eyebrow, title, intro, children, tone = 'white', testId }) => (
  <section id={id} className={`py-16 sm:py-24 ${tone === 'sand' ? 'bg-[#FAF8F5]' : 'bg-white'}`} data-testid={testId}>
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      {(eyebrow || title) && (
        <div className="max-w-2xl mb-10 sm:mb-14">
          {eyebrow && <p className={`${EYEBROW} mb-3`}>{eyebrow}</p>}
          {title && <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-stone-900 tracking-tight mb-4">{title}</h2>}
          {intro && <p className="text-base sm:text-lg text-stone-600 leading-relaxed">{intro}</p>}
        </div>
      )}
      {children}
    </div>
  </section>
);

export const Availability = ({ items, className = '' }) => (
  <ul className={`space-y-1.5 ${className}`} data-testid="availability">
    {items.map((it) => (
      <li key={it.text} className="flex items-start gap-2.5 text-sm text-stone-600">
        <span className={`w-2 h-2 mt-1.5 rounded-full flex-shrink-0 ${it.on ? 'bg-teal-600' : 'border border-stone-400'}`} />
        {it.text}
      </li>
    ))}
  </ul>
);

export const FlowSteps = ({ steps }) => (
  <ol className="relative max-w-3xl" data-testid="flow-steps">
    {steps.map((s, i) => (
      <li key={s.title} className="relative grid grid-cols-[56px_1fr] sm:grid-cols-[88px_1fr] pb-10 last:pb-0" data-testid={`flow-step-${i}`}>
        {i < steps.length - 1 && <span className="absolute left-[19px] sm:left-[23px] top-12 bottom-2 w-px bg-stone-300" />}
        <span className="text-3xl sm:text-4xl font-bold text-teal-700 leading-none tabular-nums">{i + 1}</span>
        <div className="pt-1">
          <p className="text-lg font-semibold text-stone-900 mb-1.5">{s.title}</p>
          <p className="text-base text-stone-600 leading-relaxed">{s.desc}</p>
          {s.note && <p className="mt-2 text-sm text-stone-500 italic">{s.note}</p>}
        </div>
      </li>
    ))}
  </ol>
);

export const MechanicRows = ({ rows }) => (
  <dl className="grid md:grid-cols-2 gap-x-16 gap-y-10" data-testid="mechanic-rows">
    {rows.map((r) => (
      <div key={r.title} className="border-t border-stone-200 pt-4">
        <dt className="text-base font-semibold text-stone-900 mb-2">{r.title}</dt>
        <dd className="text-base text-stone-600 leading-relaxed">{r.desc}</dd>
      </div>
    ))}
  </dl>
);

export const FAQList = ({ items }) => {
  const [open, setOpen] = useState(0);
  return (
    <div className="max-w-3xl border-t border-stone-200 divide-y divide-stone-200" data-testid="faq-list">
      {items.map((f, i) => (
        <div key={f.q}>
          <button type="button" onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between gap-6 py-5 text-left" data-testid={`faq-item-${i}`}>
            <span className="text-base sm:text-lg font-medium text-stone-900">{f.q}</span>
            <ChevronDown className={`w-5 h-5 text-stone-500 flex-shrink-0 transition-transform duration-200 ${open === i ? 'rotate-180' : ''}`} />
          </button>
          {open === i && <p className="pb-6 text-base text-stone-600 leading-relaxed">{f.a}</p>}
        </div>
      ))}
    </div>
  );
};

export const SubPageLayout = ({ active, eyebrow, title, intro, availability, primary, secondary, image, imageAlt, children }) => (
  <div className="pub-soft pub-blend min-h-screen bg-white text-stone-700">
    <PublicNav active={active} />
    <main className="pt-14">
      <section className="relative bg-[#FAF8F5] overflow-hidden" data-testid="subpage-hero">
        <img src={image} alt={imageAlt} className="hidden lg:block absolute inset-y-0 right-0 w-[42%] h-full object-cover" data-testid="subpage-hero-image" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24 lg:py-28">
          <div className="lg:max-w-[52%]">
            <p className={`${EYEBROW} mb-5`} data-testid="subpage-eyebrow">{eyebrow}</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-stone-900 tracking-tight leading-[1.05] mb-6" data-testid="subpage-title">{title}</h1>
            <p className="text-base lg:text-lg text-stone-600 leading-relaxed mb-8">{intro}</p>
            <div className="flex flex-col sm:flex-row gap-3 mb-8">
              <a href={primary.href} className="h-12 inline-flex items-center justify-center gap-2 px-7 rounded-md bg-teal-700 hover:bg-teal-800 text-white text-sm font-semibold transition-colors" data-testid="subpage-primary-cta">
                {primary.label} <ArrowRight className="w-4 h-4" />
              </a>
              {secondary && (
                <a href={secondary.href} className="h-12 inline-flex items-center justify-center px-7 rounded-md border border-stone-300 bg-white hover:bg-stone-50 text-stone-800 text-sm font-semibold transition-colors" data-testid="subpage-secondary-cta">
                  {secondary.label}
                </a>
              )}
            </div>
            <Availability items={availability} />
          </div>
        </div>
        <img src={image} alt={imageAlt} className="lg:hidden w-full h-60 sm:h-80 object-cover" />
      </section>
      {children}
      <section className="bg-teal-700 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <p className="text-xl sm:text-2xl font-semibold max-w-xl">Verify once. Add your properties. Invite your tenants.</p>
          <a href="/register" className="h-12 inline-flex items-center px-7 rounded-md bg-white text-teal-800 text-sm font-semibold hover:bg-teal-50 transition-colors flex-shrink-0" data-testid="subpage-bottom-cta">Create landlord account</a>
        </div>
      </section>
    </main>
    <PublicFooter />
  </div>
);
